import React, { useMemo, useState } from 'react';
import { Redirect, Route } from 'react-router-dom';
import PT from 'prop-types';
import { toast } from 'react-toastify';
import { getEntitiesIds } from 'Shared/util/getEntitiesIds';
import { checkFormValidation } from 'Components/Admin/Form';
import { adminMessage } from 'Components/Admin/ToastMessage';
import * as Yup from 'yup';
import { firstAndLastNameToTitle, formatToObjectOfOptions } from 'Shared/util/formatDataForSelect';
import { useMutation } from '@apollo/client';
import { mutationCreateClientContract } from '../mutations';
import watchRouteLoaded from 'Shared/util/watchRouteLoaded';

import { ADMIN_URL, FormMode } from '../../../Admin/constants/constants';
import { ActionBtnType } from '../../components/ActionMenu/ActionMenuItem/ActionMenuItem';

import FormLayout from 'Components/Admin/Form/components/FormLayout/FormLayout';
import GeneralContentForm, { projectStatuses } from './components/GeneralContentForm/GeneralContentForm';
import ContactsContentForm from './components/ContactsContentForm/ContactsContentForm';
import AssignmentsContentForm from './components/AssignmentsContentForm/AssignmentsContentForm';
import InvoiceAndOrder from './components/InvoiceAndOrder/InvoiceAndOrder';
import MobileApps from './components/MobileApps/MobileApps';
import pathToUrl from 'Shared/util/pathToUrl';
import AppSwitch from 'Components/AppSwitch';
import AdminForm from 'Components/Admin/Form/AdminForm';

import BillLargeNormalSVG from 'Components/Admin/assets/BillLargeNormalSVG';
import DocumentLargeNormalSVG from 'Components/Admin/assets/DocumentLargeNormalSVG';
import PlusSmallSVG from 'Components/Admin/assets/PlusSmallSVG';
import DownloadSVG from 'Components/Admin/assets/DownloadSVG';
import DeleteSVG from 'Components/Admin/assets/DeleteSVG';

const Tabs = {
  GENERAL: 'general',
  CONTACTS: 'contacts',
  ASSIGNMENTS: 'assignments',
  INVOICE_AND_ORDER: 'invoice-and-order',
  MOBILE: 'mobile',
};

const GeneralTab = watchRouteLoaded(GeneralContentForm);
const ContactsTab = checkFormValidation(watchRouteLoaded(ContactsContentForm));
const AssignmentsTab = checkFormValidation(watchRouteLoaded(AssignmentsContentForm));
const InvoiceAndOrderTab = checkFormValidation(watchRouteLoaded(InvoiceAndOrder));
const MobileTab = checkFormValidation(watchRouteLoaded(MobileApps));

const ProjectForm = ({ mode = FormMode.CREATE, match: { path, params } }) => {
  const [createClientContract] = useMutation(mutationCreateClientContract);

  const [tabState, setTabState] = useState({
    current: Tabs.GENERAL,
    prev: null,
  });

  const getUrl = (tab) => `${pathToUrl(path, params)}${tab}`;

  const routes = useMemo(() => ([
    { link: getUrl(Tabs.GENERAL), name: 'General', key: Tabs.GENERAL, isRequired: true },
    { link: getUrl(Tabs.CONTACTS), name: 'Contacts', key: Tabs.CONTACTS },
    { link: getUrl(Tabs.ASSIGNMENTS), name: 'Assignments', key: Tabs.ASSIGNMENTS },
    { link: getUrl(Tabs.INVOICE_AND_ORDER), name: 'Invoice and Order', key: Tabs.INVOICE_AND_ORDER },
    { link: getUrl(Tabs.MOBILE), name: 'Mobile', key: Tabs.MOBILE },
  ]), [path]);

  const menuItems = [
    [
      {
        type: ActionBtnType.BLOCK,
        title: 'Invoice',
        subTitle: 'Based on assignments for the last month.',
        icon: <BillLargeNormalSVG />,
        path: '/#',
      },
      {
        type: ActionBtnType.BLOCK,
        title: 'Blank order',
        subTitle: 'Create new blank order for this project.',
        icon: <DocumentLargeNormalSVG />,
        path: '/#',
      },
    ], [
      {
        type: ActionBtnType.LINK,
        title: 'Assign employee',
        icon: <PlusSmallSVG />,
        path: getUrl('assignments/create/'),
      },
      {
        type: ActionBtnType.LINK,
        title: 'Download PDF',
        icon: <DownloadSVG />,
      },
    ],
  ];

  const updateFormMenuItems = useMemo(() => ([
    [
      {
        type: ActionBtnType.LINK_DANGER,
        title: 'Delete project',
        icon: <DeleteSVG />,
        path: `/${ADMIN_URL}/projects/delete/${params.id}`,
      },
    ],
  ]), [params.id]);


  const tabs = {
    [Tabs.GENERAL]: {
      title: '',
      company: null,
      defaultBank: null,
      reference: '',
      defaultTerms: null,
      startDate: '',
      endDate: '',
      state: null,
      exposingBill: null,
    },
    [Tabs.CONTACTS]: {
      primaryContact: null,
      toInvoice: [],
      forOrders: [],
      forPaymentNotifications: [],
      forNotifications: [],
    },
    [Tabs.INVOICE_AND_ORDER]: {
      invoiceSubjectTemplateType: '',
      invoiceSubjectTemplate: '',
      orderSubjectTemplateType: '',
      orderSubjectTemplate: '',
      officialName: '',
      officialContractStartDate: '',
    },
    [Tabs.MOBILE]: {
      apps: {
        isMobile: false,
        isShareBuild: true,
      },
    },
  };

  const initialValues = useMemo(() => ({
    ...tabs[Tabs.GENERAL],
    ...tabs[Tabs.CONTACTS],
    ...tabs[Tabs.INVOICE_AND_ORDER],
    ...tabs[Tabs.MOBILE],
  }), [tabs]);

  const validationSchema = {
    title: Yup.string()
      .required('This field is required'),
    company: Yup.mixed()
      .required('This field is required'),
    defaultBank: Yup.mixed()
      .required('This field is required'),
    reference: Yup.string()
      .required('This field is required'),
    defaultTerms: Yup.mixed()
      .required('This field is required'),
    startDate: Yup.string()
      .required('This field is required'),
    endDate: Yup.string()
      .required('This field is required'),
    state: Yup.mixed()
      .required('This field is required'),
    exposingBill: Yup.mixed()
      .required('This field is required'),
  };

  const normalizeData = ({
                           id,
                           state,
                           defaultBank,
                           defaultTerms,
                           primaryContact,
                           toInvoice,
                           forOrders,
                           forPaymentNotifications,
                           forNotifications,
                           ...contract
                         }) => {
    const projectStatus = projectStatuses.find(({ id }) => state.toLowerCase() === id) || null;

    //TODO: Make identical project statuses on back-end

    return {
      ...formatToObjectOfOptions({
        defaultBank,
        defaultTerms,
        state: projectStatus,
        primaryContact: firstAndLastNameToTitle(primaryContact),
        toInvoice: firstAndLastNameToTitle(toInvoice),
        forOrders: firstAndLastNameToTitle(forOrders),
        forPaymentNotifications: firstAndLastNameToTitle(forPaymentNotifications),
        forNotifications: firstAndLastNameToTitle(forNotifications),
      }),
      ...contract,
    };
  };

  const handleSubmit = async ({
                                apps,
                                bill,
                                exposingBill,
                                invoiceSubjectTemplateType,
                                invoiceSubjectTemplate,
                                orderSubjectTemplateType,
                                orderSubjectTemplate,
                                ...data
                              }) => {
    const contractVariables = {
      ...data,
      ...apps,
      ...bill,
    };

    const { data: { createClientContract: { clientContract } } } = await createClientContract({
      variables: {
        input: getEntitiesIds(contractVariables, { filterEmptyItems: true }),
      },
    });

    return normalizeData(clientContract);
  };

  const handleTabLoaded = ({ tab }) => setTabState((({ current }) => ({
    current: tab,
    prev: current,
  })));

  const tabName = useMemo(() => {
    return routes.find(({ key }) => key === tabState.current).name;
  }, [tabState]);

  return (
    <div className="new-project-wrapper" data-testid={`project-form-${mode}`}>
      <AdminForm
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        tab={tabName}
      >
        {
          (form) => {
            const renderWithValidationProps = (Component, props) => {
              const validationProps = {
                redirectUrl: tabState.prev || getUrl(tabState.prev),
                baseRedirectUrl: getUrl(Tabs.GENERAL),
                fields: tabs[tabState.current],
                onInvalid: () => adminMessage({
                  title: 'You can not get to this tab.',
                  message: 'You should fill the fields on this tab correctly.',
                }, {
                  type: toast.TYPE.INFO,
                }),
                form,
              };

              return (routeProps) => (
                <Component
                  {...props}
                  {...routeProps}
                  validationProps={validationProps}
                  onRouteLoaded={handleTabLoaded}
                />
              );
            };

            return (
              <FormLayout
                routes={routes}
                menuItems={menuItems}
                updateMenuItems={mode === FormMode.UPDATE ? updateFormMenuItems : []}
              >
                <AppSwitch>
                  <Redirect exact from={path} to={getUrl(Tabs.GENERAL)} />
                  <Route
                    path={`${path}general/`}
                    render={() => <GeneralTab onRouteLoaded={handleTabLoaded} tab={Tabs.GENERAL} />}
                  />
                  <Route
                    path={`${path}contacts/`}
                    render={renderWithValidationProps(ContactsTab, { tab: Tabs.CONTACTS })}
                  />
                  <Route
                    path={`${path}assignments/`}
                    render={renderWithValidationProps(AssignmentsTab, { tab: Tabs.ASSIGNMENTS })}
                  />
                  <Route
                    path={`${path}invoice-and-order/`}
                    render={renderWithValidationProps(InvoiceAndOrderTab, { tab: Tabs.INVOICE_AND_ORDER })}
                  />
                  <Route
                    path={`${path}mobile/`}
                    render={renderWithValidationProps(MobileTab, { tab: Tabs.INVOICE_AND_ORDER })}
                  />
                </AppSwitch>
              </FormLayout>
            );
          }
        }
      </AdminForm>
    </div>
  );
};

ProjectForm.propTypes = {
  mode: PT.oneOf([FormMode.UPDATE, FormMode.CREATE]),
};

export default ProjectForm;
